import { votesApiService } from '../../votesApi';
import { relationshipsApi, RelationshipsApiError } from '../../relationshipsApi';
import { usernameCacheService } from '../../usernameCache';
import type { RelationshipGroup, RelationshipPair, RelationshipPlayerDetail, RelationshipSession } from '../../../types/relationships';
import { SkipSource, autoSection, fieldsSection, isSameUuid, monthlyVoteBuckets, type SourceDefinition } from '../helpers';

export const votesSource: SourceDefinition = {
  id: 'votes.by-user',
  title: 'Vote history',
  system: 'Vote records',
  category: 'community',
  phase: 2,
  description: 'Votes the player cast for the network on each vote site (all time) and per month. Vote records are keyed by username.',
  run: async (ctx) => {
    if (!ctx.subject.username) throw new SkipSource('no username is known for this player (vote data is keyed by username)');
    // The all-time history is served in daily buckets (the API knows hour, day and week); months are folded here.
    const [breakdown, history] = await Promise.all([votesApiService.getByUser(ctx.subject.username, 'all'), votesApiService.getHistory('all', 'day', ctx.subject.username)]);
    const points = monthlyVoteBuckets(history.data ?? []);
    const recordCount = (breakdown.total_votes ?? 0) > 0 || points.length > 0 ? 1 + breakdown.services.length + points.length : 0;
    return {
      sections: recordCount
        ? [
            fieldsSection('Totals', { username: breakdown.username, total_votes: breakdown.total_votes }, { formats: { total_votes: 'number' } }),
            autoSection('Votes per site (all time)', breakdown.services as unknown as Record<string, unknown>[], { as: 'table', formats: { vote_count: 'number' } }),
            autoSection('Votes per month', points as unknown as Record<string, unknown>[], { as: 'table', formats: { vote_count: 'number' } }),
          ]
        : [],
      recordCount,
    };
  },
};

const groupRow = (g: RelationshipGroup) => ({
  label: g.label,
  kind: g.kind,
  tier: g.tier,
  score: g.score,
  members: g.members.map((m) => m.name ?? m.uuid).join(', ') + (g.membersTruncated ? ' …' : ''),
  member_count: g.memberCount,
  families: g.families,
  common_window: g.commonWindow ? `${g.commonWindow.from} → ${g.commonWindow.until}` : null,
  server: g.server,
});

// Printed as record cards: nine table columns leave no room for a name, a UUID and a date on one line.
const pairRow = (uuid: string, p: RelationshipPair) => {
  const other = isSameUuid(p.a.uuid, uuid) ? p.b : p.a;
  return {
    other_player: other.name ?? null,
    other_uuid: other.uuid,
    score: p.score,
    days: p.days,
    families: p.families,
    eligible: p.eligible,
    reciprocal: p.reciprocal,
    evidence_count: p.evidenceCount,
    last_evidence_at: p.lastEvidenceAt,
  };
};

const sessionRow = (s: RelationshipSession) => ({ server: s.server, start: s.start ?? s.startLower, end: s.end ?? s.endUpper, quality: s.quality, restart: s.restart });

export const relationshipsSource: SourceDefinition = {
  id: 'identaware.relationships',
  title: 'Account relationships (IdentAware)',
  system: 'IdentAware',
  category: 'system',
  phase: 2,
  description: 'Analysis linking this account to others: inferred groups and observed teams, relationship scores with other players, and derived play sessions. Inferred, not declared by the player.',
  run: async (ctx) => {
    let detail: RelationshipPlayerDetail;
    try {
      detail = await relationshipsApi.player(ctx.uuid);
    } catch (e) {
      if (e instanceof RelationshipsApiError && e.status === 404) return { sections: [], recordCount: 0 };
      if (e instanceof RelationshipsApiError && (e.status === 403 || e.status === 503)) throw new SkipSource(e.message);
      throw e;
    }
    for (const a of detail.player.aliases ?? []) ctx.addAlias(a);
    const groups = detail.groups.map(groupRow);
    const pairs = detail.pairs.map((p) => pairRow(ctx.uuid, p));
    const sessions = detail.sessions.map(sessionRow);
    const notes: string[] = [];
    if (detail.groupsHasMore || detail.pairsHasMore || detail.sessionsHasMore) notes.push('Only the first page of groups, pairs or sessions is included.');
    if (detail.snapshot) notes.push(`Analysis snapshot ${detail.snapshot.analysis}/${detail.snapshot.generation}, projected ${detail.snapshot.projectedAt}.`);
    const recordCount = groups.length + pairs.length + sessions.length;
    return {
      sections: recordCount || detail.player
        ? [
            fieldsSection('Player node', { uuid: detail.player.uuid, name: detail.player.name, aliases: detail.player.aliases ?? [] }, { formats: { uuid: 'mono' } }),
            autoSection('Groups', groups, { as: 'records' }),
            autoSection('Pairs', pairs, {
              as: 'records',
              first: ['other_player', 'other_uuid', 'score', 'days', 'evidence_count', 'last_evidence_at'],
              formats: { other_uuid: 'mono', last_evidence_at: 'datetime-iso', eligible: 'bool', reciprocal: 'bool', score: 'number', evidence_count: 'number' },
              recordTitle: (row, i) => `${i + 1}. ${row.other_player ?? row.other_uuid}`,
            }),
            autoSection('Derived sessions', sessions, { as: 'table', formats: { start: 'datetime-iso', end: 'datetime-iso', restart: 'bool' } }),
          ]
        : [],
      recordCount: Math.max(recordCount, detail.player ? 1 : 0),
      notes: notes.length ? notes : undefined,
    };
  },
};

export const browserCacheSource: SourceDefinition = {
  id: 'browser.cache',
  title: 'Local browser cache',
  system: 'Browser storage on the device this report was generated from',
  category: 'system',
  phase: 2,
  description: 'UUID to username lookups are cached in each staff member\'s browser for 30 days. This is a copy on the device the report was generated from, not a server-side store.',
  run: async (ctx) => {
    const name = usernameCacheService.getName(ctx.uuid);
    if (!name) return { sections: [], recordCount: 0 };
    return { sections: [fieldsSection('usernameCache entry', { uuid: ctx.uuid, username: name, retention: '30 days from last refresh' }, { formats: { uuid: 'mono' } })], recordCount: 1 };
  },
};
